import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { buildService } from '@rd-web-markets/shared/dist/services/service';
import fetch from '@rd-web-markets/shared/dist/services/Fetcher';
import { request } from '@rd-web-markets/shared/dist/services/request';
const api = buildService('/reports', {
  only: ['all', 'get', 'create', 'update', 'delete']
}, {
  only: ['postFormData', 'putFormData']
});
const reportIssuesService = {
  useFetchAll: setLoading => {
    const [reports, setReports] = useState([]);
    const [totalPages, setTotalPages] = useState(null);
    const location = useLocation();
    const fetchReports = useErrorHandling(useCallback(async function () {
      let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      setLoading && setLoading(true);
      const response = await api.all(params);
      setReports(response?.reports || []);
      setTotalPages(Number(response?.pages) || null);
      setLoading && setLoading(false);
    }, [setLoading]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    useEffect(() => {
      const urlSearchParams = new URLSearchParams(location.search);
      const params = {
        page: urlSearchParams.get('page') || 1
      };
      fetchReports(params);
    }, [location.search, fetchReports]);
    return [reports, setReports, fetchReports, totalPages];
  },
  useFetchOne: (id, setLoading) => {
    const [report, setReport] = useState(null);
    const fetchReport = useErrorHandling(useCallback(async () => {
      setLoading && setLoading(true);
      const response = await api.get(id);
      setReport(response);
      setLoading && setLoading(false);
    }, [id, setLoading]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    useEffect(() => {
      if (id) fetchReport();
    }, [id, fetchReport]);
    return [report, setReport, fetchReport];
  },
  useCreate: (setLoading, setReports) => {
    const createReport = useErrorHandling(useCallback(async formData => {
      setLoading && setLoading(true);
      const response = await api.postFormData(formData);
      setReports(prev => [response, ...prev]);
      setLoading && setLoading(false);
      return response;
    }, [setLoading, setReports]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    return createReport;
  },
  useUpdateStatus: (setLoading, setReports) => {
    const updateStatus = useErrorHandling(useCallback(async _ref => {
      let {
        id,
        status
      } = _ref;
      setLoading && setLoading(true);
      const response = await api.update(id, {
        status
      });
      setReports(prev => prev.map(r => r.id === id ? response : r));
      setLoading && setLoading(false);
      return response;
    }, [setLoading, setReports]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    return updateStatus;
  },
  useUpdate: (setLoading, setReport) => {
    const updateReport = useErrorHandling(useCallback(async _ref2 => {
      let {
        id,
        formData
      } = _ref2;
      setLoading && setLoading(true);
      const response = await api.putFormData(id, formData);
      setReport && setReport(response);
      setLoading && setLoading(false);
      return response;
    }, [setLoading, setReport]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    return updateReport;
  },
  useDelete: (setLoading, setReports) => {
    const deleteReport = useErrorHandling(useCallback(async id => {
      setLoading && setLoading(true);
      await api.delete(id);
      setReports && setReports(prev => prev.filter(r => r.id !== id));
      setLoading && setLoading(false);
    }, [setLoading, setReports]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    return deleteReport;
  },
  useDeleteImage: setLoading => {
    const deleteImage = useErrorHandling(useCallback(async _ref3 => {
      let {
        reportId,
        imageId
      } = _ref3;
      setLoading && setLoading(true);
      const response = await fetch(`/api/reports/${reportId}/images/${imageId}`, request.delete);
      setLoading && setLoading(false);
      return response;
    }, [setLoading]), useCallback(async () => {
      setLoading && setLoading(false);
    }, [setLoading]));
    return deleteImage;
  }
};
reportIssuesService.useCrud = setLoading => {
  const [reports, setReports, fetchReports, totalPages] = reportIssuesService.useFetchAll(setLoading);
  const createReport = reportIssuesService.useCreate(setLoading, setReports);
  const updateStatus = reportIssuesService.useUpdateStatus(setLoading, setReports);
  const deleteReport = reportIssuesService.useDelete(setLoading, setReports);
  return {
    reports,
    setReports,
    fetchReports,
    totalPages,
    createReport,
    updateStatus,
    deleteReport
  };
};
export default reportIssuesService;